import React, { useState } from "react";
import {
  Smartphone,
  Tablet,
  Laptop,
  Monitor,
  X,
  ChevronDown,
  ChevronUp,
  Layers,
  Copy,
} from "lucide-react";
import type { BreakpointKey, ResponsiveCellStyle, CellStyleValues, PaddingValues } from "../types";
import { BREAKPOINTS } from "../types";

interface ResponsiveCellStyleModalProps {
  isOpen: boolean;
  responsiveStyles: ResponsiveCellStyle;
  onStylesChange: (styles: ResponsiveCellStyle) => void;
  onApply: () => void;
  onClose: () => void;
}

const paddingOptions = [
  { value: "0", label: "0" },
  { value: "2", label: "2px" },
  { value: "4", label: "4px" },
  { value: "6", label: "6px" },
  { value: "8", label: "8px" },
  { value: "10", label: "10px" },
  { value: "12", label: "12px" },
  { value: "16", label: "16px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
];

const fontSizeOptions = [
  { value: "", label: "Default" },
  { value: "10", label: "10px" },
  { value: "11", label: "11px" },
  { value: "12", label: "12px" },
  { value: "13", label: "13px" },
  { value: "14", label: "14px" },
  { value: "15", label: "15px" },
  { value: "16", label: "16px" },
  { value: "18", label: "18px" },
  { value: "20", label: "20px" },
  { value: "24", label: "24px" },
];

const fontWeightOptions = [
  { value: "", label: "Default" },
  { value: "300", label: "Light" },
  { value: "400", label: "Normal" },
  { value: "500", label: "Medium" },
  { value: "600", label: "Semi Bold" },
  { value: "700", label: "Bold" },
];

const borderWidthOptions = [
  { value: "0", label: "None" },
  { value: "1", label: "1px" },
  { value: "2", label: "2px" },
  { value: "3", label: "3px" },
  { value: "4", label: "4px" },
];

const defaultPadding: PaddingValues = { top: "8", right: "12", bottom: "8", left: "12" };

const defaultStyle: CellStyleValues = {
  backgroundColor: "#ffffff",
  textColor: "#000000",
  borderColor: "#e5e7eb",
  borderWidth: "1",
  padding: defaultPadding,
  fontSize: "",
  fontWeight: "",
  textAlign: "left",
};

const breakpointIcons: Record<BreakpointKey, React.FC<{ className?: string }>> = {
  xs: Smartphone,
  sm: Tablet,
  md: Laptop,
  lg: Monitor,
  xl: Monitor,
};

export const ResponsiveCellStyleModal: React.FC<ResponsiveCellStyleModalProps> = ({
  isOpen,
  responsiveStyles,
  onStylesChange,
  onApply,
  onClose,
}) => {
  const [expandedBreakpoint, setExpandedBreakpoint] = useState<BreakpointKey>("xs");

  if (!isOpen) return null;

  const updateStyleForBreakpoint = (
    breakpoint: BreakpointKey,
    field: keyof CellStyleValues,
    value: any
  ) => {
    onStylesChange({
      ...responsiveStyles,
      [breakpoint]: {
        ...responsiveStyles[breakpoint],
        [field]: value,
      },
    });
  };

  const updatePaddingForBreakpoint = (
    breakpoint: BreakpointKey,
    side: keyof PaddingValues,
    value: string
  ) => {
    const currentPadding = responsiveStyles[breakpoint]?.padding || defaultPadding;
    onStylesChange({
      ...responsiveStyles,
      [breakpoint]: {
        ...responsiveStyles[breakpoint],
        padding: {
          ...currentPadding,
          [side]: value,
        },
      },
    });
  };

  const copyToAllBreakpoints = (sourceBreakpoint: BreakpointKey) => {
    const sourceStyle = responsiveStyles[sourceBreakpoint] || defaultStyle;
    const newStyles: ResponsiveCellStyle = {
      xs: { ...sourceStyle },
      sm: { ...sourceStyle },
      md: { ...sourceStyle },
      lg: { ...sourceStyle },
      xl: { ...sourceStyle },
    };
    onStylesChange(newStyles);
  };

  const getStyleValue = (breakpoint: BreakpointKey, field: keyof CellStyleValues) => {
    return responsiveStyles[breakpoint]?.[field] ?? defaultStyle[field];
  };

  const getPaddingValue = (breakpoint: BreakpointKey, side: keyof PaddingValues) => {
    return responsiveStyles[breakpoint]?.padding?.[side] ?? defaultPadding[side];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Responsive Cell Styles
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Configure different cell styles for each screen size. This allows you to have completely
          different appearances on mobile (340px-1079px) vs desktop.
        </p>

        {/* Breakpoint Tabs */}
        <div className="flex border-b mb-4 overflow-x-auto">
          {(Object.keys(BREAKPOINTS) as BreakpointKey[]).map((key) => {
            const Icon = breakpointIcons[key];
            const bp = BREAKPOINTS[key];
            const isActive = expandedBreakpoint === key;

            return (
              <button
                key={key}
                type="button"
                onClick={() => setExpandedBreakpoint(key)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? "border-primary-main text-primary-main"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {bp.label.split(" ")[0]}
              </button>
            );
          })}
        </div>

        {/* Current Breakpoint Info */}
        <div className="bg-blue-50 px-4 py-2 rounded-md mb-4 flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-blue-800">
              {BREAKPOINTS[expandedBreakpoint].label}
            </span>
            <span className="text-xs text-blue-600 ml-2">
              ({BREAKPOINTS[expandedBreakpoint].description})
            </span>
          </div>
          <button
            type="button"
            onClick={() => copyToAllBreakpoints(expandedBreakpoint)}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
          >
            <Copy className="w-3 h-3" />
            Copy to all breakpoints
          </button>
        </div>

        {/* Style Controls */}
        <div className="space-y-4">
          {/* Colors */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Background</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={getStyleValue(expandedBreakpoint, "backgroundColor") as string || "#ffffff"}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "backgroundColor", e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={getStyleValue(expandedBreakpoint, "backgroundColor") as string || ""}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "backgroundColor", e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#ffffff"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={getStyleValue(expandedBreakpoint, "textColor") as string || "#000000"}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "textColor", e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={getStyleValue(expandedBreakpoint, "textColor") as string || ""}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "textColor", e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Color</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={getStyleValue(expandedBreakpoint, "borderColor") as string || "#e5e7eb"}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "borderColor", e.target.value)}
                  className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={getStyleValue(expandedBreakpoint, "borderColor") as string || ""}
                  onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "borderColor", e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-sm"
                  placeholder="#e5e7eb"
                />
              </div>
            </div>
          </div>

          {/* Font & Border */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Size</label>
              <select
                value={getStyleValue(expandedBreakpoint, "fontSize") as string || ""}
                onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "fontSize", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                {fontSizeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
              <select
                value={getStyleValue(expandedBreakpoint, "fontWeight") as string || ""}
                onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "fontWeight", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                {fontWeightOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Border Width</label>
              <select
                value={getStyleValue(expandedBreakpoint, "borderWidth") as string || "1"}
                onChange={(e) => updateStyleForBreakpoint(expandedBreakpoint, "borderWidth", e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                {borderWidthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Text Alignment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Text Alignment</label>
            <div className="flex gap-2">
              {(["left", "center", "right"] as const).map((align) => (
                <button
                  key={align}
                  type="button"
                  onClick={() => updateStyleForBreakpoint(expandedBreakpoint, "textAlign", align)}
                  className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all capitalize ${
                    getStyleValue(expandedBreakpoint, "textAlign") === align
                      ? "bg-primary-main text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {align}
                </button>
              ))}
            </div>
          </div>

          {/* Padding */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Padding (px)</label>
            <div className="grid grid-cols-4 gap-3">
              {(["top", "right", "bottom", "left"] as const).map((side) => (
                <div key={side}>
                  <label className="block text-xs text-gray-500 mb-1 capitalize">{side}</label>
                  <select
                    value={getPaddingValue(expandedBreakpoint, side)}
                    onChange={(e) => updatePaddingForBreakpoint(expandedBreakpoint, side, e.target.value)}
                    className="w-full border border-gray-300 px-2 py-2 rounded text-sm bg-white"
                  >
                    {paddingOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="border-t pt-4 mt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            Preview ({BREAKPOINTS[expandedBreakpoint].label})
          </h4>
          <div className="bg-gray-100 p-4 rounded">
            <table className="border-collapse w-full">
              <tbody>
                <tr>
                  <td
                    style={{
                      backgroundColor: getStyleValue(expandedBreakpoint, "backgroundColor") as string || "#ffffff",
                      color: getStyleValue(expandedBreakpoint, "textColor") as string || "#000000",
                      borderColor: getStyleValue(expandedBreakpoint, "borderColor") as string || "#e5e7eb",
                      borderWidth: `${getStyleValue(expandedBreakpoint, "borderWidth") || "1"}px`,
                      borderStyle: "solid",
                      fontSize: getStyleValue(expandedBreakpoint, "fontSize") ? `${getStyleValue(expandedBreakpoint, "fontSize")}px` : undefined,
                      fontWeight: getStyleValue(expandedBreakpoint, "fontWeight") as string || undefined,
                      textAlign: getStyleValue(expandedBreakpoint, "textAlign") as "left" | "center" | "right" || "left",
                      padding: `${getPaddingValue(expandedBreakpoint, "top")}px ${getPaddingValue(expandedBreakpoint, "right")}px ${getPaddingValue(expandedBreakpoint, "bottom")}px ${getPaddingValue(expandedBreakpoint, "left")}px`,
                    }}
                  >
                    Sample cell content for {BREAKPOINTS[expandedBreakpoint].label}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-main/90 transition-all"
          >
            Apply Responsive Styles
          </button>
        </div>
      </div>
    </div>
  );
};

